"use client";

import { Checkbox, RadioButton } from "@afa-shared/afa-components";
import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Select from "@afa-shared/afa-components/dist/Inputs/Select";
import TextInput from "@afa-shared/afa-components/dist/Inputs/TextInput";
import Text from "@afa-shared/afa-components/dist/Text";
import { FormProps } from "@components/FormBlock";
import { StandardLink } from "@components/Link/StandardLink";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import { useId, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

import FormErrorFeedback from "../Feedback/FormErrorFeedback";
import { FormSendFeedback } from "../Feedback/FormSendFeedback";
import FormSuccessFeedback from "../Feedback/FormSuccessFeedback";
import FormValidationFeedback from "../Feedback/FormValidationFeedback";
import formStyles from "../FormStyles/forms.module.css";
import { emailMaxInputLength, generalMaxInputLength, phoneMaxInputLength } from "../formConstants";
import { scrollIntoView, validate } from "../formHelper";
import { trackFormStart, trackFormSubmit } from "../formTracker";
import {
  BIRTH_YEAR_RANGE_OPTIONS,
  EMPLOYER_OPTIONS,
  MUNICIPALITY_OPTIONS,
  PROFILE_CHECKBOX_OPTIONS,
} from "./options";

enum Gender {
  FEMALE = "Kvinna",
  MALE = "Man",
  OTHER = "Annat/vill inte svara",
}

enum EmploymentTypes {
  PERMANENT = "Fast anställning",
  FIXED_TERM = "Tidsbegränsad anställning",
  ON_DEMAND = "Behovsstyrd anställning/timanställd",
}

enum NativeTongue {
  BEGINNER = "Nybörjare",
  BASIC_UNDERSTANDING = "Grundläggande förståelse",
  GOOD_UNDERSTANDING = "God förståelse",
  FLUENT = "Flytande",
}

export const KRWebForm = ({ contentfulPreviewEntryId }: FormProps) => {
  const [displayForm, setDisplayForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  const [isValidToken, setIsValidToken] = useState(false);
  const [errorSendMail, setErrorSendMail] = useState(false);
  const [hasSingleError, setHasSingleError] = useState(false);

  // Form state: keep value + required flags in one place
  const [formState, setFormState] = useState({
    firstName: { value: "", required: true },
    lastName: { value: "", required: true },
    email: { value: "", required: true },
    phone: { value: "", required: true },
    location: { value: "", required: true },
    birthRange: { value: "", required: true },
    gender: { value: "", required: true },
    nativeTongue: { value: "", required: true },
    role: { value: "", required: false },
    employer: { value: "", required: true },
    employmentType: { value: "", required: false },
    profiles: { value: [] as string[], required: false },
  });

  // Errors map: include all fields that can error/scroll
  const [errors, setErrors] = useState<Record<string, any>>({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    location: null,
    birthRange: null,
    role: null,
    gender: null,
    nativeTongue: null,
    employer: null,
    employmentType: null,
    profiles: null,
  });

  // Refs for focusing on error fields
  const formRefs = {
    sendFeedback: useRef<HTMLDivElement | null>(null),
    errorInfobox: useRef<HTMLDivElement | null>(null),
    firstName: useRef<HTMLInputElement | null>(null),
    lastName: useRef<HTMLInputElement | null>(null),
    email: useRef<HTMLInputElement | null>(null),
    location: useRef<HTMLSelectElement | null>(null),
    role: useRef<HTMLInputElement | null>(null),
    employer: useRef<HTMLSelectElement | null>(null),
    birthRange: useRef<HTMLSelectElement | null>(null),
    phone: useRef<HTMLInputElement | null>(null),
    nativeTongue: useRef<HTMLFieldSetElement | null>(null),
    gender: useRef<HTMLFieldSetElement | null>(null),
    employmentType: useRef<HTMLFieldSetElement | null>(null),
    profiles: useRef<HTMLFieldSetElement | null>(null),
  };

  const formIds = {
    firstName: useId(),
    lastName: useId(),
    email: useId(),
    phone: useId(),
    location: useId(),
    birthRange: useId(),
    role: useId(),
    employer: useId(),
    header: useId(),
    nativeTongue: useId(),
  };

  // Toggle a checkbox profile
  const handleCheckboxClick = (id: string) => {
    const exists = formState.profiles.value.includes(id);
    setFormState((prev) => ({
      ...prev,
      profiles: {
        value: exists ? prev.profiles.value.filter((v) => v !== id) : [...prev.profiles.value, id],
        required: false,
      },
    }));
    setErrors((prev) => ({ ...prev, profiles: null }));
  };

  const getProfileLabelsFromIds = (ids: string[]): string[] =>
    ids.map((id) => PROFILE_CHECKBOX_OPTIONS.find((o) => o.id === id)?.label?.toLowerCase() ?? "");

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: keyof typeof formState,
  ) => {
    if (!formStarted) {
      trackFormStart("feedbackgruppen");
      setFormStarted(true);
    }
    const { value } = e.target;
    setFormState((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // Radio setter (for non-event sources)
  const setFieldValue = (key: keyof typeof formState, value: any) => {
    setFormState((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setHasSingleError(false);
    const formErrors = validate(formState as any, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((v) => v !== null).length;

    if (foundErrors) {
      if (foundErrors === 1) {
        setHasSingleError(true);
        const key = Object.keys(formErrors).find((k) => formErrors[k] !== null)!;
        scrollIntoView(formRefs[key]);
      } else {
        scrollIntoView(formRefs.errorInfobox);
      }
      return;
    }

    const cleanFormState = Object.entries(formState).reduce<Record<string, any>>(
      (acc, [k, v]: [string, any]) => {
        //Profiles should use PROFILE_CHECKBOX_OPTIONS labels (Swedish naming)
        if (k === "profiles" && Array.isArray(v.value)) {
          acc[k] = v.value.map(
            // eslint-disable-next-line sonarjs/no-nested-functions
            (id: string) => PROFILE_CHECKBOX_OPTIONS.find((o) => o.id === id)?.label ?? id,
          );
        } else {
          acc[k] = v.value;
        }
        return acc;
      },
      {},
    );

    setIsLoading(true);
    try {
      const res = await fetch("/api/kr-testpanel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanFormState),
      });

      if (res.ok) {
        setDisplayForm(true);
        formRefs?.sendFeedback?.current?.focus();
        trackFormSubmit("feedbackgruppen", getProfileLabelsFromIds(formState.profiles.value));
      } else {
        setErrorSendMail(true);
        setDisplayForm(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReCaptchaVerify = (token?: string) => {
    if (!token) return;
    setIsValidToken(true);
  };

  const renderErrorInfoBox = () => (
    <div ref={formRefs.errorInfobox}>
      <FormValidationFeedback
        formErrors={errors}
        formRefs={formRefs}
        hasSingleError={hasSingleError}
      />
    </div>
  );

  const renderFormFeedback = () => (
    <FormSuccessFeedback
      messageHeading="Tack!"
      messageChildren={
        <Text variant="paragraph" removeMargin>
          Din information har skickats.
        </Text>
      }
      messageLink={
        <StandardLink text="Så behandlar vi personuppgifter" href={"/personuppgifter"} />
      }
    />
  );

  const renderErrorMessage = () => <FormErrorFeedback />;

  const showFormFeedback = displayForm && !errorSendMail;
  const personuppgifterPath = "/personuppgifter";

  return (
    <Grid
      direction="column"
      className="form-container"
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <GoogleReCaptcha onVerify={handleReCaptchaVerify} />

      <FormSendFeedback
        formSendRef={formRefs.sendFeedback}
        formErrorFeedback={renderErrorMessage()}
        formSuccessFeedback={renderFormFeedback()}
        showSuccessFeedback={showFormFeedback}
        showErrorFeedback={errorSendMail}
      />

      {!displayForm && (
        <div className={`${formStyles.formBackground} ${formStyles.formWrapper}`}>
          <div className={formStyles.formHeadingWrapper}>
            <Heading id={formIds.header} variant="h2" removeMargin>
              Intresseanmälan
            </Heading>
            <Text variant="paragraph" removeMargin>
              * Obligatoriskt fält
            </Text>
          </div>

          {renderErrorInfoBox()}

          <form aria-labelledby={formIds.header} noValidate className={formStyles.form}>
            <TextInput
              inputRef={formRefs.firstName}
              label="Ditt förnamn *"
              id={formIds.firstName}
              name="firstName"
              autoComplete="given-name"
              value={formState.firstName.value}
              type="text"
              maxLength={generalMaxInputLength}
              error={errors.firstName?.input}
              fullWidth
              onChange={(e) => handleFormChange(e, "firstName")}
              required
            />

            <TextInput
              inputRef={formRefs.lastName}
              label="Ditt efternamn *"
              id={formIds.lastName}
              name="lastName"
              autoComplete="family-name"
              value={formState.lastName.value}
              type="text"
              maxLength={generalMaxInputLength}
              error={errors.lastName?.input}
              fullWidth
              onChange={(e) => handleFormChange(e, "lastName")}
              required
            />

            <TextInput
              inputRef={formRefs.email}
              label="Din e-postadress *"
              id={formIds.email}
              name="email"
              autoComplete="email"
              value={formState.email.value}
              type="email"
              maxLength={emailMaxInputLength}
              width="100%"
              error={errors.email?.input}
              onChange={(e) => handleFormChange(e, "email")}
              required
            />

            <TextInput
              inputRef={formRefs.phone}
              label="Ditt telefonnummer *"
              id={formIds.phone}
              name="phone"
              autoComplete="tel"
              width="100%"
              maxLength={phoneMaxInputLength}
              value={formState.phone.value}
              type="tel"
              error={errors.phone?.input}
              onChange={(e) => handleFormChange(e, "phone")}
              required
            />

            <Select
              inputRef={formRefs.location}
              labelText="I vilken kommun bor du? *"
              options={MUNICIPALITY_OPTIONS}
              id={formIds.location}
              selected={formState.location.value}
              placeholder="Välj kommun"
              error={errors.location?.input}
              onChange={(e) => handleFormChange(e as any, "location")}
              width="100%"
              required
            />

            <Select
              inputRef={formRefs.birthRange}
              labelText="När är du född? *"
              options={BIRTH_YEAR_RANGE_OPTIONS}
              id={formIds.birthRange}
              selected={formState.birthRange.value}
              placeholder="Välj det årsintervall då du är född"
              error={errors.birthRange?.input}
              onChange={(e) => handleFormChange(e as any, "birthRange")}
              width="100%"
              required
            />

            <fieldset className={formStyles.radioWrapper}>
              <legend className={formStyles.radioWrapper__legend}>Kön *</legend>
              <RadioButton
                ref={formRefs.gender}
                label="Kvinna"
                name="gender"
                value={Gender.FEMALE}
                onChange={() => setFieldValue("gender", Gender.FEMALE)}
                checked={formState.gender.value === Gender.FEMALE}
                required
              />
              <RadioButton
                label="Man"
                name="gender"
                value={Gender.MALE}
                onChange={() => setFieldValue("gender", Gender.MALE)}
                checked={formState.gender.value === Gender.MALE}
              />
              <RadioButton
                label="Annat/vill inte svara"
                name="gender"
                value={Gender.OTHER}
                onChange={() => setFieldValue("gender", Gender.OTHER)}
                checked={formState.gender.value === Gender.OTHER}
              />
              {errors.gender?.input && (
                <p id="gender-error" className={formStyles.radioError}>
                  {errors.gender.input}
                </p>
              )}
            </fieldset>

            <fieldset className={formStyles.radioWrapper}>
              <legend className={formStyles.radioWrapper__legend}>
                Hur skulle du beskriva din kunskapsnivå när det gäller att läsa texter på svenska? *
              </legend>

              <RadioButton
                ref={formRefs.nativeTongue}
                label="Nybörjare"
                name="nativeTongue"
                value={NativeTongue.BEGINNER}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.BEGINNER)}
                checked={formState.nativeTongue.value === NativeTongue.BEGINNER}
                required
              />
              <RadioButton
                label="Grundläggande förståelse"
                name="nativeTongue"
                value={NativeTongue.BASIC_UNDERSTANDING}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.BASIC_UNDERSTANDING)}
                checked={formState.nativeTongue.value === NativeTongue.BASIC_UNDERSTANDING}
              />
              <RadioButton
                label="God förståelse"
                name="nativeTongue"
                value={NativeTongue.GOOD_UNDERSTANDING}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.GOOD_UNDERSTANDING)}
                checked={formState.nativeTongue.value === NativeTongue.GOOD_UNDERSTANDING}
              />
              <RadioButton
                label="Flytande"
                name="nativeTongue"
                value={NativeTongue.FLUENT}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.FLUENT)}
                checked={formState.nativeTongue.value === NativeTongue.FLUENT}
              />
              {errors.nativeTongue?.input && (
                <p id="native-tongue-error" className={formStyles.radioError}>
                  {errors.nativeTongue.input}
                </p>
              )}
            </fieldset>

            <TextInput
              inputRef={formRefs.role}
              label="Vad har du för yrke/roll?"
              id={formIds.role}
              name="role"
              width="100%"
              maxLength={generalMaxInputLength}
              value={formState.role.value}
              type="text"
              error={errors.role?.input}
              onChange={(e) => handleFormChange(e, "role")}
            />

            <Select
              inputRef={formRefs.employer}
              labelText="Var är du anställd? *"
              options={EMPLOYER_OPTIONS}
              id={formIds.employer}
              selected={formState.employer.value}
              placeholder="Välj arbetsgivare"
              error={errors.employer?.input}
              onChange={(e) => handleFormChange(e as any, "employer")}
              width="100%"
              required
            />

            <fieldset className={formStyles.radioWrapper}>
              <legend className={formStyles.radioWrapper__legend}>
                Vad har du för typ av anställning?
                <p className={formStyles.radioWrapper__hint}>
                  Hoppa över frågan om du inte har en anställning
                </p>
              </legend>

              <RadioButton
                ref={formRefs.employmentType}
                label="Fast anställning"
                name="employmentType"
                value={EmploymentTypes.PERMANENT}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.PERMANENT)}
                checked={formState.employmentType.value === EmploymentTypes.PERMANENT}
              />
              <RadioButton
                label="Tidsbegränsad anställning"
                name="employmentType"
                value={EmploymentTypes.FIXED_TERM}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.FIXED_TERM)}
                checked={formState.employmentType.value === EmploymentTypes.FIXED_TERM}
              />
              <RadioButton
                label="Behovsstyrd anställning/timanställd"
                name="employmentType"
                value={EmploymentTypes.ON_DEMAND}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.ON_DEMAND)}
                checked={formState.employmentType.value === EmploymentTypes.ON_DEMAND}
              />
            </fieldset>

            <fieldset className={formStyles.radioWrapper}>
              <legend className={formStyles.radioWrapper__legend}>
                Har du någon av följande profiler?
                <p className={formStyles.radioWrapper__hint}>
                  Hoppa över frågan om du inte vill svara, eller om ingen av profilerna stämmer in
                  på dig
                </p>
              </legend>

              {PROFILE_CHECKBOX_OPTIONS.map((option) => (
                <Checkbox
                  ref={formRefs.profiles}
                  key={option.id}
                  id={option.id}
                  label={option.label}
                  onChange={() => handleCheckboxClick(option.id)}
                  checked={formState.profiles.value.includes(option.id)}
                />
              ))}
            </fieldset>

            <StandardLink href={personuppgifterPath} text="Så behandlar vi personuppgifter" />

            <Button
              type="submit"
              buttonType="primary"
              onClick={isValidToken ? onSubmit : (e) => e.preventDefault()}
              label={isLoading ? "" : "Skicka"}
              className={clsx(formStyles.submitButton, { [formStyles.spinner]: isLoading })}
              disabled={isLoading}
            />
          </form>
        </div>
      )}
    </Grid>
  );
};

export default KRWebForm;
